/**
 * src/components/layout/ServiceRestartBanner.jsx
 *
 * App-shell notice while the backend restarts (see api/serviceRestart.js).
 * Driven by the 'seasuite:service-restarting' window event; clears itself when
 * the waiting calls get an answer.
 *
 * THE SENTENCE IS IN THE TENSE THE CODE CAN KEEP (GH #7669). It used to read
 * "your changes are kept and will save automatically", which `serviceRestart.js`
 * contradicts in its own header: "Nothing is persisted: closing the tab
 * mid-restart loses a queued write." The write lives only in memory, it is lost
 * at RESTART_MAX_WAIT_MS if the platform has not come back, and the claim was
 * made on ENTERING the wait — before any retry had been attempted at all.
 *
 * That is `storage.json`'s own `mustNeverClaim` ("that an edit the user made is
 * stored when it is not") and STG-B-003's crossing ("an unacknowledged write is
 * unconfirmed, never confirmed"). This banner is also app-wide and ref-counted,
 * so one waiting call makes its promise on behalf of every unsaved edit in the
 * session, including edits in modules it knows nothing about — which is why the
 * wording has to be the conservative one rather than the reassuring one.
 *
 * Making "are kept" true is a separate piece of work (durable queueing of
 * in-flight writes, the shape `src/state/teardownBackup.js` already provides
 * for state writes). It is not queued, and it must not hold this correction.
 */

import { useState, useEffect } from 'react';
import { RESTART_EVENT } from '../../api/serviceRestart.js';

export default function ServiceRestartBanner() {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const onEvent = (e) => setActive(!!e?.detail?.active);
    window.addEventListener(RESTART_EVENT, onEvent);
    return () => window.removeEventListener(RESTART_EVENT, onEvent);
  }, []);

  if (!active) return null;

  return (
    <div
      role="status"
      style={{
        background: 'var(--warn-bg, rgba(245,158,11,0.10))',
        borderBottom: '1px solid var(--warn)',
        color: 'var(--warn)',
        padding: '8px 20px',
        fontSize: '13px',
        fontFamily: 'var(--font-mono)',
        flexShrink: 0,
        zIndex: 200,
      }}
    >
      <span style={{ fontWeight: 600 }}>Service is restarting</span>
      {
        ' — your change has not been saved yet. We are retrying; leave this tab open or it will be lost.'
      }
    </div>
  );
}
